import {
  PlayCircle,
  Globe,
  Server,
  Hexagon,
  Database,
  HardDrive,
  Settings,
  ShieldCheck,
  Activity,
  Mail,
  type LucideIcon,
} from 'lucide-react';
import { brand } from '@/config/brand';
import { paths } from '@/lib/paths';
import { SERVICE_SCOPES } from '@/lib/service-scopes';

/**
 * Service catalog taxonomy — ported from the legacy @console ServiceDashboard
 * `SERVICE_CATEGORIES`, re-skinned for @vnetwork/ui v2 (lucide icons). Categories
 * group providers; a provisioned service maps to a category by its provider
 * scope (`categoryOfScope`).
 */

export type ProductStatus = 'active' | 'beta' | 'coming_soon';

export interface ServiceProduct {
  name: string; // animal key (legacy)
  displayName: string;
  scope: string; // provider_scope
  description: string;
  /** Hover-tooltip copy — a fuller one-liner than `description`; falls back to it. */
  tip?: string;
  features: string[];
  status: ProductStatus;
  /**
   * Platform features that aren't provisioned per-service (no provider scope)
   * link straight into their own console instead of a catalog landing page.
   */
  consoleRoute?: string;
  /**
   * Animal codename of the provider a first-time activation provisions on, when
   * the product is self-serve. Present → the catalog CTA activates the service
   * (backend mints the tenant) instead of a contact-sales inquiry. The catalog
   * identity (name/scope) and the activation provider can differ — Multi-CDN is
   * catalogued as `owl` but activates on `raven` (vEdge, the consolidation edge).
   */
  activateProvider?: string;
  /**
   * Hide this product from the catalog tiles (and the "Available" count) so it is
   * never advertised as a new self-serve activation. It is NOT removed from the
   * taxonomy: an org that already holds the service still sees its provisioned
   * card and opens its console. Used for streaming products that are only
   * provisioned by ops — the catalog surfaces just Media Transcode.
   */
  catalogHidden?: boolean;
}

export interface ServiceCategory {
  key: string;
  title: string;
  icon: LucideIcon;
  description: string;
  products: ServiceProduct[];
  /** Optional highlight pill shown on the sidebar entry (e.g. "Popular"). When set,
   *  it takes the badge slot instead of the provisioned-count number. */
  badge?: string;
  /** Dedicated category landing page (e.g. the Compute one). When set, the
   *  sidebar entry opens this hub instead of the query-filtered dashboard
   *  (`/services?category=<key>`). */
  landingRoute?: string;
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    key: 'streaming',
    title: 'Media & Streaming',
    icon: PlayCircle,
    description: 'Live playout, transcoding and video delivery.',
    landingRoute: '/services/streaming',
    products: [
      {
        name: 'parrot',
        displayName: 'FastChannel',
        scope: 'Internal.FastChannel',
        description: 'Free ad-supported streaming TV with scheduling, playout and EPG.',
        tip: 'Builds linear FAST channels from your library, with scheduling, ad breaks and an EPG feed for aggregators.',
        features: ['Scheduling', 'Playout', 'VOD Integration', 'EPG'],
        status: 'active',
        // Catalog surfaces only Media Transcode; the rest show once provisioned.
        catalogHidden: true,
      },
      {
        name: 'penguin',
        displayName: 'Live Streaming',
        // Penguin/LMS live services carry Internal.HLSLowLatency on the backend.
        scope: 'Internal.HLSLowLatency',
        description: 'Enterprise live streaming (LMS) with live transcoding, DRM and multi-CDN delivery.',
        tip: 'Ingests RTMP/SRT, transcodes to an ABR ladder, and delivers over multiple CDNs with DRM applied.',
        features: ['Live Transcoding', 'DRM', 'Multi-CDN', 'Low Latency'],
        status: 'active',
        // Self-serve: provisions on the `penguin` provider (LMS/FlashCone).
        // On success → /services/live/:id
        activateProvider: 'penguin',
        // Catalog surfaces only Media Transcode; shown only once provisioned.
        catalogHidden: true,
      },
      {
        name: 'lens',
        displayName: 'Media Service',
        scope: 'External.SwiftCoder',
        description: 'On-demand video (VOD) transcoding, packaging and adaptive-bitrate streaming.',
        tip: 'Upload a master file; it is transcoded, packaged to HLS/DASH and served from the edge.',
        features: ['VOD', 'Transcoding', 'Packaging', 'Adaptive Bitrate'],
        status: 'active',
        // Self-serve: provisions on the `lens` provider (VMS/SwiftCoder).
        // On success → /services/vod/:id
        activateProvider: 'lens',
        // Catalog surfaces only Media Transcode; shown only once provisioned.
        catalogHidden: true,
      },
      {
        name: 'peacock',
        displayName: 'On-Demand Transcoding',
        scope: 'Internal.Transcoding',
        description: 'On-demand transcoding to multiple formats, resolutions and thumbnails.',
        tip: 'Job-based transcoding API: submit a source file, get renditions, thumbnails and watermarked outputs.',
        features: ['Multiple Formats', 'Adaptive Bitrate', 'Thumbnails', 'Watermarking'],
        status: 'coming_soon',
      },
      {
        name: 'pangolin',
        displayName: 'AES Gateway',
        scope: 'Internal.Aes',
        description: 'HLS AES-128 key gateway for VOD and live, with metrics and logs.',
        tip: 'Serves AES-128 keys for HLS with per-session authorization, so encrypted playback stays auditable.',
        features: ['HLS AES-128', 'VOD + LIVE', 'Live logs', 'Metrics'],
        status: 'beta',
        // Provisioned per service → routed via serviceRoute(scope, id) to
        // /services/aes/:serviceId (no static consoleRoute).
        // Catalog surfaces only Media Transcode; shown only once provisioned.
        catalogHidden: true,
      },
      {
        name: 'marlin',
        displayName: 'Media Transcode Service',
        scope: 'Internal.MediaTranscode',
        description: 'Live ingest, adaptive-bitrate transcoding and multi-format delivery (HLS/DASH/WHEP).',
        features: ['Live Ingest', 'Transcoding', 'HLS + DASH', 'DVR + Protection'],
        status: 'beta',
        // Platform console: onboarding + resources handled inside its own module
        // (workspace gate + /tenant/* proxy), so it links straight to its console.
        consoleRoute: paths.mediaV2.base,
      },
    ],
  },
  {
    key: 'cdn',
    title: 'Networking & CDN',
    icon: Globe,
    description: 'Edge delivery, caching, WAF and TLS for your domains.',
    landingRoute: '/services/cdn',
    products: [
      {
        name: 'owl',
        displayName: 'Multi-CDN Orchestration',
        scope: 'External.Mlytics',
        description: 'Orchestrate delivery across multiple CDN backends with WAF and TLS.',
        tip: 'Routes traffic across multiple CDN vendors and fails over automatically when one degrades.',
        features: ['Domains', 'Analytics', 'WAF', 'SSL/TLS'],
        status: 'active',
        activateProvider: 'raven',
      },
    ],
  },
  {
    key: 'compute',
    title: 'Compute',
    icon: Server,
    description: 'Virtual machines and bare-metal compute.',
    landingRoute: paths.compute.base,
    products: [
      {
        name: 'panda',
        displayName: 'Cloud Compute',
        scope: 'Internal.Opennebula',
        description: 'Virtual machines with networking, load balancers and backups.',
        tip: 'General-purpose virtual machines billed hourly, with private networking and scheduled snapshots.',
        features: ['VMs', 'Networking', 'Load Balancers', 'Snapshots'],
        status: 'active',
        // Self-serve: provisions on the `panda` provider (OpenNebula/Techev Cloud).
        activateProvider: 'panda',
      },
      {
        name: 'bear',
        displayName: 'Enterprise Compute',
        scope: 'Internal.VMware',
        description: 'Dedicated virtual machines with high availability and monitoring.',
        tip: 'Single-tenant hosts with HA placement and committed resources, for workloads that cannot share hardware.',
        features: ['VMs', 'Monitoring', 'High Availability'],
        status: 'active',
      },
      {
        name: 'cheetah',
        displayName: 'Serverless Functions',
        scope: 'Internal.Functions',
        description: 'Run code without servers, with auto-scaling and pay-per-use.',
        tip: 'Deploy a function instead of a server. It scales to zero and you pay only per invocation.',
        features: ['Auto-scaling', 'Pay-per-use', 'API Gateway'],
        status: 'coming_soon',
      },
      {
        name: 'weaver',
        displayName: 'Hosting',
        scope: 'External.Hosting',
        description: 'Your websites on VNETWORK hosting: PHP, SSL, DNS, logs, databases and FTP.',
        features: ['PHP', 'SSL', 'Databases', 'FTP'],
        status: 'beta',
        // Platform console, like Business Mail: activation and resources are handled inside the
        // module (workspace gate on the margarita/weaver service), so it links straight to it.
        consoleRoute: paths.hosting.base,
      },
    ],
  },
  {
    key: 'kubernetes',
    title: 'Kubernetes',
    icon: Hexagon,
    description: 'Managed clusters and container tooling.',
    landingRoute: paths.kubernetes.base,
    products: [
      {
        name: 'wolf',
        displayName: 'Managed Kubernetes',
        scope: 'Internal.Opennebula.Kubernetes',
        description: 'Managed clusters with node pools, ingress and auto-scaling.',
        tip: 'Managed control plane and worker nodes: you deploy workloads, we run the cluster.',
        features: ['Clusters', 'Node Pools', 'Ingress', 'Auto-scaling'],
        status: 'active',
        // Self-serve: provisions on the `wolf` provider (Managed Kubernetes).
        activateProvider: 'wolf',
      },
      {
        name: 'husky',
        displayName: 'Container Registry',
        scope: 'Internal.ContainerRegistry',
        description: 'Private container image storage with vulnerability scanning.',
        tip: 'Private OCI registry with layer dedup, CVE scanning on push and retention policies per repository.',
        features: ['Image Storage', 'Scanning', 'Access Control'],
        status: 'coming_soon',
      },
    ],
  },
  {
    key: 'databases',
    title: 'Databases',
    icon: Database,
    description: 'Managed relational, cache and document databases.',
    landingRoute: '/services/databases',
    products: [
      {
        name: 'elephant',
        displayName: 'Database as a Service',
        scope: 'Internal.Opennebula.Database',
        description: 'Managed MySQL, PostgreSQL, MariaDB and Redis with auto backups.',
        features: ['RDS', 'Redis', 'Auto Backups', 'Monitoring'],
        status: 'active',
      },
    ],
  },
  {
    key: 'storage',
    title: 'Storage',
    icon: HardDrive,
    description: 'S3-compatible object storage.',
    landingRoute: '/services/storage',
    products: [
      {
        name: 'walrus',
        displayName: 'Object Storage',
        scope: 'Internal.Minio',
        description: 'S3-compatible object buckets with access keys and versioning.',
        features: ['Buckets', 'Access Keys', 'Versioning'],
        status: 'beta',
        // No consoleRoute: like every non-WAAP product it goes through the catalog
        // detail → contact-sales inquiry when not provisioned. A provisioned Minio
        // service still opens its console via serviceRoute (/services/oss/:id).
      },
      // Block storage is already delivered as Compute Volumes (SSD volumes +
      // snapshots), so no standalone "Block Storage" catalog product here.
    ],
  },
  {
    key: 'observability',
    title: 'Observability',
    icon: Activity,
    description: 'Uptime monitoring, latency tracking and incident alerting.',
    landingRoute: '/services/observability',
    products: [
      {
        name: 'heron',
        displayName: 'Healthcheck & Alerting',
        scope: 'Internal.Healthcheck',
        description: 'Monitor endpoints, ports and certificates with multi-channel alerting.',
        tip: 'Probes endpoints from multiple regions and alerts by email, webhook or chat when checks fail.',
        features: ['Uptime', 'Latency', 'TLS expiry', 'Alerting'],
        status: 'beta',
        consoleRoute: paths.healthcheck.base,
      },
    ],
  },
  {
    key: 'management',
    title: 'Management',
    icon: Settings,
    description: 'Identity, organizations, billing and API keys.',
    landingRoute: '/services/management',
    products: [
      {
        name: 'griffin',
        displayName: 'Organizations',
        scope: 'Management.Organizations',
        description: 'Projects, teams and resources with fine-grained permissions.',
        tip: 'Groups resources into projects and teams, with role-based permissions scoped per project.',
        features: ['Projects', 'Teams', 'Permissions'],
        status: 'active',
        consoleRoute: paths.organizations.base,
      },
      {
        name: 'unicorn',
        displayName: 'Usage & Cost Management',
        scope: 'Management.Billing',
        description: 'Invoices, payment methods and detailed usage reports.',
        tip: 'Line-item usage per service with exportable invoices and budget alerts before spend overruns.',
        features: ['Invoices', 'Payments', 'Usage'],
        status: 'coming_soon',
      },
    ],
  },
  {
    key: 'security',
    title: 'Security',
    icon: ShieldCheck,
    description: 'WAF, certificates and secrets.',
    badge: 'Popular',
    landingRoute: '/services/security',
    products: [
      {
        name: 'cobra',
        displayName: 'Web Application & API Protection',
        scope: 'Security.WAAP',
        description:
          'Web Application & API Protection: AI-WAF, bot management, rate limiting and API security for your websites.',
        tip: 'WAF rules, bot mitigation and rate limiting applied at the edge before traffic reaches your origin.',
        features: ['AI-WAF', 'Bot Management', 'Rate Limiting', 'API Protection'],
        status: 'active',
        // Self-serve: a first-time activation provisions on the standalone
        // `cobra` provider (Conversant WAAP), separate from the eagle CDN.
        activateProvider: 'cobra',
        // Routes to the WAAP landing gate (mirrors healthcheck): it shows the
        // activation hero when the org has no WAAP service, else redirects into
        // the console — instead of the generic catalog locked-preview.
        consoleRoute: paths.waap.base,
      },
      {
        name: 'lion',
        displayName: 'Web Application Firewall',
        scope: 'Security.WAF',
        description: 'DDoS protection with OWASP rules and bot management.',
        tip: 'Managed OWASP Top-10 rule set with tunable sensitivity, plus volumetric attack absorption.',
        features: ['DDoS', 'OWASP', 'Bot Management'],
        status: 'coming_soon',
      },
      {
        name: 'tiger',
        displayName: 'SSL/TLS Certificates',
        scope: 'Security.Certificates',
        description: 'Issue, renew and manage certificates with auto-renewal.',
        tip: 'Issues and auto-renews certificates over ACME, including wildcards, and pins the minimum TLS version.',
        features: ['Auto-renewal', 'Wildcard', 'ACME'],
        status: 'beta',
        consoleRoute: paths.certinvent.base,
      },
    ],
  },
  {
    key: 'mail',
    title: 'Business Mail',
    icon: Mail,
    description: 'Business email on your own domain.',
    landingRoute: paths.mail.base,
    products: [
      {
        name: 'pigeon',
        displayName: 'Business Mail',
        scope: 'External.VMail',
        description: 'Business email on your own domain: mailboxes, groups, webmail and anti-spam.',
        features: ['Custom Domain', 'Mailboxes', 'Groups', 'Webmail'],
        status: 'beta',
        // Platform console, like Media Transcode: activation and resources are
        // handled inside the module (workspace gate on the margarita/mail
        // service), so it links straight to its console.
        consoleRoute: paths.mail.base,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Top-level nav groups
// ---------------------------------------------------------------------------

/** A labelled cluster of categories — the uppercase headings in the sidebar. */
export interface ServiceGroup {
  heading: string;
  /** Category keys under this group, in display order. */
  categoryKeys: string[];
}

/**
 * The top-level groups the services sidebar clusters categories under
 * (Cloudflare-style section headings). Every catalog category belongs to exactly
 * one group; order here drives the sidebar order.
 */
export const SERVICE_GROUPS: ServiceGroup[] = [
  { heading: 'Protect', categoryKeys: ['security'] },
  { heading: 'Infrastructure', categoryKeys: ['compute', 'kubernetes', 'databases', 'storage'] },
  { heading: 'Delivery', categoryKeys: ['streaming', 'cdn'] },
  { heading: 'Communication', categoryKeys: ['mail'] },
  { heading: 'Observe & Manage', categoryKeys: ['observability', 'management'] },
];

// ---------------------------------------------------------------------------
// Scope → category, route, icon
// ---------------------------------------------------------------------------

// Which category a provisioned service falls under is declared by the module that serves the
// scope (`serviceScopes` in its `service.ts`, merged by `src/app/service-scopes.ts`). Only scopes no
// module owns stay here: catalog-only products with no console, and CDN vendors that ride the
// shared CDN shell (vEdge/EdgeNext/Raven/Hawk all use the Manticore CDN UI).
const SHELL_SCOPE_CATEGORY: Record<string, string> = {
  'Internal.Transcoding': 'streaming',
  'Internal.VOD': 'streaming',
  'External.Hawk': 'cdn',
  'External.Bunny.CDN': 'cdn',
  'External.Conversant.CDN': 'cdn',
  'External.Aliyun.CDN': 'cdn',
};

const SCOPE_CATEGORY: Record<string, string> = {
  ...SHELL_SCOPE_CATEGORY,
  ...Object.fromEntries(SERVICE_SCOPES.map((s) => [s.scope, s.category])),
};

const SCOPE_ROUTE = new Map(SERVICE_SCOPES.flatMap((s) => (s.route ? [[s.scope, s.route] as const] : [])));

/** Map a provider scope to a category key (or "other"). */
export function categoryOfScope(scope: string | undefined): string {
  if (!scope) return 'other';
  return SCOPE_CATEGORY[scope] ?? 'other';
}

// ── Brand product allowlist ────────────────────────────────────────────────
// A whitelabel brand may offer only a subset of products. `brand.services` lists
// the enabled product codenames (ServiceProduct.name); undefined = all products.

/** Whether the active brand offers this product (by codename). */
export function isProductEnabled(name: string): boolean {
  return !brand.services || brand.services.includes(name);
}

/** True when the brand offers at least one product in this category. */
export function isCategoryEnabled(cat: ServiceCategory): boolean {
  return cat.products.some((p) => isProductEnabled(p.name));
}

/** True when the brand offers the category a provisioned scope belongs to.
 *  Unknown / "other" scopes are never hidden (a provisioned service always shows). */
export function isScopeCategoryEnabled(scope: string | undefined): boolean {
  const cat = categoryByKey(categoryOfScope(scope));
  return cat ? isCategoryEnabled(cat) : true;
}

/**
 * SERVICE_CATEGORIES filtered to the active brand: categories with no enabled
 * product are dropped, and each remaining category keeps only enabled products.
 * Returns the original array unchanged when the brand allows everything.
 */
/** Whether a product is shown as a catalog tile: not coming-soon, allowlisted,
 *  and not flagged catalog-hidden (provisioned-only, never advertised new). */
export function isProductVisible(p: ServiceProduct): boolean {
  return p.status !== 'coming_soon' && !p.catalogHidden && isProductEnabled(p.name);
}

export function brandServiceCategories(): ServiceCategory[] {
  return SERVICE_CATEGORIES.map((c) => ({
    ...c,
    products: c.products.filter(isProductVisible),
  })).filter((c) => c.products.length > 0);
}

/** A single category with only the products visible to the active brand — for
 *  the per-category landing pages (e.g. /services/compute). */
export function brandCategoryByKey(key: string): ServiceCategory | undefined {
  const c = categoryByKey(key);
  return c ? { ...c, products: c.products.filter(isProductVisible) } : undefined;
}

/**
 * Customer-facing canonical pick order when an org holds multiple CDN provider
 * services. vEdge (Raven) is the consolidation destination so it wins; the rest
 * fall back by delivery preference, then alphabetically (handled by the caller).
 */
export const CANONICAL_CDN_SCOPE_PRIORITY = [
  'External.Raven', // vEdge — primary
  'External.SwiftFederation', // Manticore / Eagle
  'External.Hawk', // EdgeNext
  'External.Bunny.CDN', // Bunny.net
  'External.Conversant.CDN',
  'External.Aliyun.CDN',
  'External.Mlytics',
] as const;

/**
 * Where a provisioned service opens. Only the surfaces the console actually
 * implements return a route; everything else is provisioned-but-not-yet-wired.
 */
export function serviceRoute(scope: string | undefined, serviceId: string): string | null {
  if (!scope) return null;
  // A module's own console first (Owl/Mlytics opens its WAF/CDN console though it is a cdn scope).
  const route = SCOPE_ROUTE.get(scope);
  if (route) return route(serviceId);
  // Every other cdn scope opens the shared CDN shell.
  if (categoryOfScope(scope) === 'cdn') return `/services/cdn/${serviceId}`;
  return null;
}

const CATEGORY_BY_KEY: Record<string, ServiceCategory> = Object.fromEntries(
  SERVICE_CATEGORIES.map((c) => [c.key, c]),
);

export function categoryByKey(key: string): ServiceCategory | undefined {
  return CATEGORY_BY_KEY[key];
}

/**
 * Where a service console / catalog page's "Back" should land. When the owning
 * category has its own landing page (Compute, Security), return there so the
 * user goes back to the category they came from; otherwise fall back to the
 * all-services dashboard. Prevents every console from dumping the user on "All
 * services" regardless of where they entered.
 */
export function categoryBackTarget(categoryKey: string | undefined): { path: string; label: string } {
  const cat = categoryKey ? CATEGORY_BY_KEY[categoryKey] : undefined;
  return cat?.landingRoute
    ? { path: cat.landingRoute, label: `Back to ${cat.title}` }
    : { path: '/services', label: 'Back to Services' };
}

const PRODUCT_BY_SCOPE: Record<string, ServiceProduct> = Object.fromEntries(
  SERVICE_CATEGORIES.flatMap((c) => c.products.map((p) => [p.scope, p])),
);
// Penguin/LMS live services appear under two backend scopes in the wild
// (`Internal.HLSLowLatency` and `External.SwiftCoder.LMS`) — alias both to the
// same Live Media Service product so either provisioned card shows its details.
PRODUCT_BY_SCOPE['External.SwiftCoder.LMS'] = PRODUCT_BY_SCOPE['Internal.HLSLowLatency'];

/** Catalog product for a provider scope — lets a provisioned card reuse the
 * product's description + features so active services look as complete as
 * catalog ones. Multi-CDN umbrellas resolve via the canonical `External.Mlytics`. */
export function productByScope(scope: string | undefined): ServiceProduct | undefined {
  return scope ? PRODUCT_BY_SCOPE[scope] : undefined;
}

/**
 * URL of a catalog product's landing page. Segments are dot-free (category key +
 * animal name) on purpose — provider scopes contain dots (e.g. `Internal.Transcoding`),
 * and a dotted last path segment makes the dev server's history fallback treat it
 * as a static file and 404 on deep-link / hard refresh.
 */
export function catalogPath(categoryKey: string, productName: string): string {
  return `/services/catalog/${categoryKey}/${productName}`;
}

/** Resolve a catalog product from its (category key, animal name) URL segments. */
export function findCatalogProduct(
  categoryKey: string | undefined,
  productName: string | undefined,
): { product: ServiceProduct; category: ServiceCategory } | null {
  const category = categoryKey ? CATEGORY_BY_KEY[categoryKey] : undefined;
  if (!category) return null;
  const product = category.products.find((p) => p.name === productName);
  return product ? { product, category } : null;
}

/** Icon for a service, by its category. */
export function iconForScope(scope: string | undefined): LucideIcon {
  return CATEGORY_BY_KEY[categoryOfScope(scope)]?.icon ?? Globe;
}
